import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { FaHome, FaGraduationCap, FaQuestionCircle, FaChartLine, FaComments, FaPlus, FaHeart, FaEdit, FaTrash, FaArrowLeft, FaFilter, FaTrophy, FaAward, FaFlask } from 'react-icons/fa';
import AuthService from '../services/AuthService';
import ForumService from '../services/ForumService';
import { isGuestUser } from '../utils/guestUtils';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple, createPulse, createSparkle, createGlow, createSuccessBurst } from '../utils/vfx';
import BottomNav from './BottomNav';
import ProfileMenu from './ProfileMenu';
import './Forum.css';

const Forum = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const { state, actions } = useUser();
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('newest');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostCategory, setNewPostCategory] = useState('general');
  const [newComment, setNewComment] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const authCheckedRef = useRef(false);
  const userLeagueIdRef = useRef(null);
  const isGuest = isGuestUser(state.user);

  // Check auth only on mount and when postId changes
  useEffect(() => {
    const checkAuth = async () => {
      // Prevent multiple simultaneous auth checks
      if (authCheckedRef.current) return;
      
      if (!AuthService.isAuthenticated() || !state.user) {
        navigate('/login', { replace: true });
        return;
      }

      authCheckedRef.current = true;
      const currentLeagueId = state.user?.leagueId || null;
      userLeagueIdRef.current = currentLeagueId;

      // Verify student status with backend to ensure we have latest data
      // This prevents students from accessing forum even if frontend state is stale
      try {
        const response = await AuthService.validateToken();
        if (response.valid && response.user) {
          const newLeagueId = response.user.leagueId || null;
          
          // Only update user if leagueId actually changed AND we haven't already set it
          if (newLeagueId !== currentLeagueId && newLeagueId !== userLeagueIdRef.current) {
            userLeagueIdRef.current = newLeagueId;
            // Update user context without triggering re-render loop
            actions.setUser({
              ...response.user,
              leagueId: newLeagueId
            });
          }

          // Block student accounts from accessing forum
          if (newLeagueId) {
            toast.error('Student accounts do not have access to forums');
            navigate('/dashboard', { replace: true });
            authCheckedRef.current = false;
            return;
          }
        }
      } catch (error) {
        // If verification fails, check current state
        if (currentLeagueId) {
          toast.error('Student accounts do not have access to forums');
          navigate('/dashboard', { replace: true });
          authCheckedRef.current = false;
          return;
        }
      }

      // Double-check with current state as well
      if (currentLeagueId) {
        toast.error('Student accounts do not have access to forums');
        navigate('/dashboard', { replace: true });
        authCheckedRef.current = false;
        return;
      }

      setIsAuthenticated(true);
      soundEffects.playPageTransition();

      // Guests keep the forum tab but only see the account-required gate,
      // so skip loading any forum data for them
      if (isGuestUser(state.user)) {
        setLoading(false);
        setTimeout(() => {
          authCheckedRef.current = false;
        }, 1000);
        return;
      }

      if (postId) {
        loadPost(postId);
      } else {
        // Clear selected post when navigating back to list
        setSelectedPost(null);
        setComments([]);
        loadPosts();
      }
      
      // Reset auth check flag after a delay to allow for future checks
      setTimeout(() => {
        authCheckedRef.current = false;
      }, 1000);
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, postId]); // Only run on mount and when postId changes

  // Separate effect to handle student account blocking without causing reload loop
  useEffect(() => {
    if (isAuthenticated && state.user?.leagueId) {
      toast.error('Student accounts do not have access to forums');
      navigate('/dashboard', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user?.leagueId]); // Only check when leagueId changes

  useEffect(() => {
    if (isAuthenticated && !postId && !isGuest) {
      loadPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sort, isAuthenticated, postId]); // Added postId to dependencies

  useEffect(() => {
    if (showLeaderboard && isAuthenticated && !isGuest) {
      loadLeaderboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLeaderboard, isAuthenticated]); // loadLeaderboard is stable, no need to include

  const loadLeaderboard = async () => {
    try {
      const data = await ForumService.getLeaderboard(10);
      setLeaderboard(data.leaderboard || []);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    }
  };

  const handleMarkAsContribution = async (postId, e) => {
    e?.stopPropagation();
    
    if (!window.confirm('Mark this post as a contribution? This will award 1 point to the post author.')) {
      return;
    }

    try {
      await ForumService.markAsContribution(postId);
      toast.success('Post marked as contribution!');
      soundEffects.playAchievement();
      if (e?.currentTarget) {
        createSparkle(e.currentTarget, 10);
      }
      
      // Reload posts to show updated status
      if (selectedPost && selectedPost.id === postId) {
        loadPost(postId);
      } else {
        loadPosts();
      }
      
      // Reload leaderboard if visible
      if (showLeaderboard) {
        loadLeaderboard();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to mark as contribution');
      soundEffects.playError();
    }
  };

  const isAdmin = state.user?.isAdmin || false;

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await ForumService.getPosts(category || null, sort, 1, 50);
      setPosts(data.posts || []);
    } catch (error) {
      toast.error('Failed to load forum posts');
    } finally {
      setLoading(false);
    }
  };

  const loadPost = async (id) => {
    try {
      setLoading(true);
      const data = await ForumService.getPost(id);
      setSelectedPost(data.post);
      setComments(data.comments || []);
    } catch (error) {
      toast.error('Failed to load post');
      navigate('/forum');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (e) => {
    if (!newPostTitle.trim() || !newPostContent.trim()) {
      toast.error('Please fill in all fields');
      soundEffects.playError();
      return;
    }

    try {
      const post = await ForumService.createPost(newPostTitle, newPostContent, newPostCategory);
      toast.success('Post created successfully!');
      soundEffects.playSuccess();
      if (e?.currentTarget) {
        createSuccessBurst(e.currentTarget);
      }
      setShowCreateModal(false);
      setNewPostTitle('');
      setNewPostContent('');
      setNewPostCategory('general');
      loadPosts();
    } catch (error) {
      toast.error(error.message || 'Failed to create post');
      soundEffects.playError();
    }
  };

  const handleLikePost = async (postId, e) => {
    e?.stopPropagation();
    try {
      const result = await ForumService.likePost(postId);
      if (result.liked) {
        soundEffects.playSuccess();
        if (e?.currentTarget) {
          createSparkle(e.currentTarget, 5);
        }
      }
      if (selectedPost && selectedPost.id === postId) {
        setSelectedPost({
          ...selectedPost,
          likes: result.liked ? selectedPost.likes + 1 : selectedPost.likes - 1,
          userLiked: result.liked
        });
      } else {
        loadPosts();
      }
    } catch (error) {
      toast.error('Failed to like post');
      soundEffects.playError();
    }
  };

  const handleCreateComment = async (e) => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      soundEffects.playError();
      return;
    }

    try {
      const comment = await ForumService.createComment(selectedPost.id, newComment);
      setComments([...comments, comment]);
      setNewComment('');
      toast.success('Comment added!');
      soundEffects.playSuccess();
      if (e?.currentTarget) {
        createRipple(e);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to add comment');
      soundEffects.playError();
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      const result = await ForumService.likeComment(commentId);
      if (result.liked) {
        soundEffects.playSuccess();
      }
      setComments(comments.map(comment => 
        comment.id === commentId 
          ? { ...comment, likes: result.liked ? comment.likes + 1 : comment.likes - 1, userLiked: result.liked }
          : comment
      ));
    } catch (error) {
      toast.error('Failed to like comment');
      soundEffects.playError();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Helper function to check if user can delete a post
  const canDeletePost = (post) => {
    if (!state.user) return false;
    
    // User owns the post
    if (post.userId === state.user.id) return true;
    
    // Check if user is admin level 2 or higher
    const getAdminLevelNumber = (level) => {
      if (!level) return 0;
      if (level === 'master_admin') return 999;
      const num = parseInt(level);
      return isNaN(num) ? 0 : num;
    };
    
    const adminLevel = state.user.adminLevel;
    const isAdmin = state.user.isAdmin || false;
    const userLevel = getAdminLevelNumber(adminLevel);
    
    // Admin level 2+ can delete any post
    return isAdmin && userLevel >= 2;
  };

  const handleDeletePost = async (postId, e) => {
    e?.stopPropagation(); // Prevent navigation when clicking delete
    
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      await ForumService.deletePost(postId);
      toast.success('Post deleted successfully');
      soundEffects.playPop();
      
      // If we're viewing the deleted post, navigate back to forum list
      if (selectedPost && selectedPost.id === postId) {
        setSelectedPost(null);
        setComments([]);
        navigate('/forum');
      } else {
        // Reload posts list
        loadPosts();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete post');
      soundEffects.playError();
    }
  };

  const handleBackClick = (e) => {
    soundEffects.playClick();
    createRipple(e);
    setSelectedPost(null);
    setComments([]);
    setTimeout(() => navigate('/forum'), 200);
  };

  if (!isAuthenticated) {
    return null;
  }

  // Guest gate: the forum tab stays visible, but the page itself asks for a real account
  if (isGuest) {
    return (
      <div className="forum-container">
        <div className="forum-top-bar">
          <div className="forum-top-content">
            <button onClick={(e) => { soundEffects.playClick(); createRipple(e); navigate('/dashboard'); }} onMouseEnter={() => soundEffects.playHover()} className="forum-back-btn">
              ←
            </button>
            <h1 className="forum-title">Forum</h1>
            <ProfileMenu />
          </div>
        </div>

        <div className="forum-main-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="forum-guest-gate"
          >
            <FaComments className="forum-guest-gate-icon" />
            <h2 className="forum-guest-gate-title">Make an actual account</h2>
            <p className="forum-guest-gate-text">
              The forum is for members only. You're browsing as a guest — create a
              free account to read posts and join the conversation.
            </p>
            <button
              onClick={(e) => { soundEffects.playClick(); createRipple(e); navigate('/signup'); }}
              onMouseEnter={() => soundEffects.playHover()}
              className="forum-guest-gate-btn"
            >
              Make an Actual Account
            </button>
          </motion.div>
        </div>

        <BottomNav />
      </div>
    );
  }

  if (loading && !selectedPost) {
    return (
      <div className="forum-container">
        <div className="forum-loading">
          <div className="forum-spinner"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Post detail view
  if (selectedPost) {
    return (
      <div className="forum-container">
        <div className="forum-top-bar">
          <div className="forum-top-content">
            <button onClick={handleBackClick} className="forum-back-btn">
              <FaArrowLeft />
            </button>
            <h1 className="forum-title">Forum</h1>
            <ProfileMenu />
          </div>
        </div>

        <div className="forum-main-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="forum-post-detail"
          >
            <div className="forum-post-header">
              <div className="forum-post-meta">
                <h2 className="forum-post-title">{selectedPost.title}</h2>
                <div className="forum-post-info">
                  <span className="forum-author">{selectedPost.userName || 'Anonymous'}</span>
                  <span className="forum-date">{formatDate(selectedPost.createdAt)}</span>
                  <span className="forum-category">{selectedPost.category}</span>
                </div>
              </div>
              <div className="forum-post-actions">
                {selectedPost.isContribution && (
                  <span className="forum-contribution-badge" title="This post is marked as a contribution">
                    <FaAward /> Contribution
                  </span>
                )}
                <button
                  onClick={(e) => handleLikePost(selectedPost.id, e)}
                  onMouseEnter={() => soundEffects.playHover()}
                  className={`forum-like-btn ${selectedPost.userLiked ? 'liked' : ''}`}
                >
                  <FaHeart /> {selectedPost.likes || 0}
                </button>
                {isAdmin && !selectedPost.isContribution && (
                  <button
                    onClick={(e) => {
                      soundEffects.playClick();
                      createRipple(e);
                      handleMarkAsContribution(selectedPost.id, e);
                    }}
                    onMouseEnter={() => soundEffects.playHover()}
                    className="forum-contribution-btn"
                    title="Mark as contribution"
                  >
                    <FaAward /> Mark Contribution
                  </button>
                )}
                {canDeletePost(selectedPost) && (
                  <button
                    onClick={(e) => {
                      soundEffects.playError();
                      handleDeletePost(selectedPost.id, e);
                    }}
                    onMouseEnter={() => soundEffects.playHover()}
                    className="forum-delete-btn"
                    title="Delete post"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            </div>
            <div className="forum-post-content">{selectedPost.content}</div>

            <div className="forum-comments-section">
              <h3 className="forum-comments-title">Comments ({comments.length})</h3>
              <div className="forum-comment-form">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="forum-comment-input"
                />
                <button onClick={handleCreateComment} className="forum-comment-btn">
                  Post Comment
                </button>
              </div>
              <div className="forum-comments-list">
                {comments.map((comment) => (
                  <motion.div
                    key={comment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="forum-comment"
                  >
                    <div className="forum-comment-header">
                      <span className="forum-comment-author">{comment.userName || 'Anonymous'}</span>
                      <span className="forum-comment-date">{formatDate(comment.createdAt)}</span>
                    </div>
                    <div className="forum-comment-content">{comment.content}</div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        soundEffects.playClick();
                        createRipple(e);
                        handleLikeComment(comment.id);
                      }}
                      onMouseEnter={() => soundEffects.playHover()}
                      className={`forum-comment-like ${comment.userLiked ? 'liked' : ''}`}
                    >
                      <FaHeart /> {comment.likes || 0}
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <BottomNav />
      </div>
    );
  }

  // Posts list view
  return (
    <div className="forum-container">
      <div className="forum-top-bar">
        <div className="forum-top-content">
          <button onClick={(e) => { soundEffects.playClick(); createRipple(e); navigate('/dashboard'); }} onMouseEnter={() => soundEffects.playHover()} className="forum-back-btn">
            ←
          </button>
          <h1 className="forum-title">Forum</h1>
          <div className="forum-top-actions">
            <button
              onClick={(e) => {
                soundEffects.playClick();
                createRipple(e);
                setShowLeaderboard(!showLeaderboard);
              }}
              onMouseEnter={() => soundEffects.playHover()}
              className="forum-leaderboard-btn"
            >
              <FaTrophy /> Leaderboard
            </button>
            <button onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
              setShowCreateModal(true);
            }} onMouseEnter={() => soundEffects.playHover()} className="forum-create-btn">
              <FaPlus /> New Post
            </button>
            <ProfileMenu />
          </div>
        </div>
      </div>

      <div className="forum-main-content">
        {showLeaderboard && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="forum-leaderboard-card"
          >
            <div className="forum-leaderboard-header">
              <h2><FaTrophy /> Contributions Leaderboard</h2>
                <button onClick={(e) => {
                  soundEffects.playClick();
                  createRipple(e);
                  setShowLeaderboard(false);
                }} onMouseEnter={() => soundEffects.playHover()} className="forum-close-btn">×</button>
            </div>
            <div className="forum-leaderboard-list">
              {leaderboard.length === 0 ? (
                <p className="forum-empty-text">No contributions yet</p>
              ) : (
                leaderboard.map((user, index) => (
                  <div key={user.id} className="forum-leaderboard-item">
                    <div className="forum-leaderboard-rank">
                      {index === 0 && <span className="rank-gold">🥇</span>}
                      {index === 1 && <span className="rank-silver">🥈</span>}
                      {index === 2 && <span className="rank-bronze">🥉</span>}
                      {index > 2 && <span className="rank-number">#{index + 1}</span>}
                    </div>
                    <div className="forum-leaderboard-user">
                      <span className="forum-leaderboard-name">{user.name || user.email}</span>
                      <span className="forum-leaderboard-points">{user.points || 0} points</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
        <div className="forum-filters">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="forum-filter-select"
          >
            <option value="">All Categories</option>
            <option value="general">General</option>
            <option value="trading">Trading</option>
            <option value="learning">Learning</option>
            <option value="questions">Questions</option>
            <option value="tips">Tips</option>
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="forum-filter-select"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="popular">Popular</option>
          </select>
        </div>

        <div className="forum-posts-list">
          {posts.length === 0 ? (
            <div className="forum-empty">
              <FaComments className="forum-empty-icon" />
              <p>No posts yet. Be the first to post!</p>
            </div>
          ) : (
            posts.map((post) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="forum-post-card"
                onClick={(e) => {
                  soundEffects.playClick();
                  createRipple(e);
                  setTimeout(() => navigate(`/forum/${post.id}`), 200);
                }}
                onMouseEnter={(e) => {
                  soundEffects.playHover();
                  createPulse(e.currentTarget, 1.01, 200);
                }}
              >
                <div className="forum-post-card-header">
                  <h3 className="forum-post-card-title">{post.title}</h3>
                  <div className="forum-post-card-header-right">
                    {post.isContribution && (
                      <span className="forum-post-card-contribution" title="Contribution">
                        <FaAward />
                      </span>
                    )}
                    <span className="forum-post-card-category">{post.category}</span>
                    {isAdmin && !post.isContribution && (
                      <button
                        onClick={(e) => handleMarkAsContribution(post.id, e)}
                        className="forum-post-card-contribution-btn"
                        title="Mark as contribution"
                      >
                        <FaAward />
                      </button>
                    )}
                    {canDeletePost(post) && (
                      <button
                        onClick={(e) => handleDeletePost(post.id, e)}
                        className="forum-post-card-delete-btn"
                        title="Delete post"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </div>
                <div className="forum-post-card-meta">
                  <span className="forum-post-card-author">{post.userName || 'Anonymous'}</span>
                  <span className="forum-post-card-date">{formatDate(post.createdAt)}</span>
                </div>
                <div className="forum-post-card-stats">
                  <span className="forum-post-card-likes">
                    <FaHeart /> {post.likes || 0}
                  </span>
                  <span className="forum-post-card-comments">
                    <FaComments /> {post.commentCount || 0}
                  </span>
                  <span className="forum-post-card-views">
                    👁️ {post.views || 0}
                  </span>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {showCreateModal && (
        <div className="forum-modal-overlay" onClick={(e) => {
          soundEffects.playClick();
          setShowCreateModal(false);
        }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="forum-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="forum-modal-title">Create New Post</h2>
            <input
              type="text"
              value={newPostTitle}
              onChange={(e) => setNewPostTitle(e.target.value)}
              placeholder="Post title..."
              className="forum-modal-input"
            />
            <select
              value={newPostCategory}
              onChange={(e) => setNewPostCategory(e.target.value)}
              className="forum-modal-select"
            >
              <option value="general">General</option>
              <option value="trading">Trading</option>
              <option value="learning">Learning</option>
              <option value="questions">Questions</option>
              <option value="tips">Tips</option>
            </select>
            <textarea
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              placeholder="Write your post..."
              className="forum-modal-textarea"
              rows={8}
            />
            <div className="forum-modal-actions">
              <button
                onClick={(e) => {
                  soundEffects.playClick();
                  createRipple(e);
                  setShowCreateModal(false);
                }}
                onMouseEnter={() => soundEffects.playHover()}
                className="forum-modal-btn forum-modal-btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePost}
                onMouseEnter={() => soundEffects.playHover()}
                className="forum-modal-btn forum-modal-btn-primary"
              >
                Post
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default Forum;

